
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import { UserCalendarEvent } from '../api/entities.js';
import { Ticket } from '../api/entities.js';
import { Project } from '../api/entities.js';
import { format, startOfWeek, addDays, startOfMonth, endOfMonth, isSameMonth, isSameDay, addMonths, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  X,
  Check,
  ArrowRight
} from 'lucide-react';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Checkbox } from "../components/ui/checkbox.jsx";
import { Label } from "../components/ui/label.jsx";
import { Switch } from "../components/ui/switch.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.jsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover.jsx";

// Cores para tipos de eventos
const EVENT_COLORS = {
  ticket: 'bg-blue-500 hover:bg-blue-600',
  project: 'bg-purple-500 hover:bg-purple-600',
  meeting: 'bg-amber-500 hover:bg-amber-600',
  internal: 'bg-green-500 hover:bg-green-600',
  external: 'bg-rose-500 hover:bg-rose-600',
  other: 'bg-gray-500 hover:bg-gray-600'
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [view, setView] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [showEventDetailModal, setShowEventDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_date: new Date(),
    end_date: new Date(),
    all_day: false,
    color: '#6366F1',
    related_type: 'internal',
    related_id: '',
    participants: [],
    status: 'scheduled',
    notification: true,
    notification_time: 15,
    location: ''
  });
  const [relatedItems, setRelatedItems] = useState({
    tickets: [],
    projects: []
  });
  const [eventType, setEventType] = useState('meeting');

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadCalendarData();
    }
  }, [currentUser, currentDate, view]);

  const loadUserData = async () => {
    try {
      const userData = await User.me();
      setCurrentUser(userData);
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  };

  const loadCalendarData = async () => {
    setIsLoading(true);
    try {
      const startDate = startOfMonth(currentDate);
      const endDate = endOfMonth(currentDate);
      
      // Carregar eventos do usuário
      const userEvents = await UserCalendarEvent.filter({
        user_id: currentUser.id
      });
      
      // Carregar eventos onde o usuário é participante
      const participantEvents = await UserCalendarEvent.list();
      const filteredParticipantEvents = participantEvents.filter(
        event => event.participants && event.participants.includes(currentUser.id)
      );
      
      // Combinar e remover duplicatas
      const allEvents = [...userEvents, ...filteredParticipantEvents];
      const uniqueEvents = Array.from(new Map(allEvents.map(event => [event.id, event])).values());
      
      setEvents(uniqueEvents);
      
      // Carregar chamados e projetos para relacionamento
      const [tickets, projects] = await Promise.all([
        Ticket.list(),
        Project.list()
      ]);
      
      setRelatedItems({
        tickets,
        projects
      });
    } catch (error) {
      console.error('Erro ao carregar dados do calendário:', error);
    }
    setIsLoading(false);
  };

  const handleCreateEvent = async () => {
    try {
      if (!newEvent.title) {
        alert('Por favor, preencha o título do evento.');
        return;
      }
      
      // Formatar datas para ISO string
      const formattedEvent = {
        ...newEvent,
        user_id: currentUser.id,
        start_date: new Date(newEvent.start_date).toISOString(),
        end_date: new Date(newEvent.end_date).toISOString()
      };
      
      await UserCalendarEvent.create(formattedEvent);
      setShowNewEventModal(false);
      loadCalendarData();
      
      // Limpar formulário
      setNewEvent({
        title: '',
        description: '',
        start_date: new Date(),
        end_date: new Date(),
        all_day: false,
        color: '#6366F1',
        related_type: 'internal',
        related_id: '',
        participants: [],
        status: 'scheduled',
        notification: true,
        notification_time: 15,
        location: ''
      });
    } catch (error) {
      console.error('Erro ao criar evento:', error);
      alert('Ocorreu um erro ao criar o evento. Por favor, tente novamente.');
    }
  };

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setShowEventDetailModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (confirm('Tem certeza que deseja excluir este evento?')) {
      try {
        await UserCalendarEvent.delete(eventId);
        setShowEventDetailModal(false);
        loadCalendarData();
      } catch (error) {
        console.error('Erro ao excluir evento:', error);
        alert('Ocorreu um erro ao excluir o evento. Por favor, tente novamente.');
      }
    }
  };

  const renderCalendarMonth = () => {
    const startDate = startOfMonth(currentDate);
    const endDate = endOfMonth(currentDate);
    const startDay = getDay(startDate);
    
    // Dias da semana
    const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    
    // Dias do mês
    let days = [];
    let day = startOfWeek(startDate, { weekStartsOn: 0 });
    
    for (let i = 0; i < 42; i++) {
      const cloneDay = new Date(day);
      days.push(cloneDay);
      day = addDays(day, 1);
    }
    
    return (
      <div className="w-full">
        <div className="grid grid-cols-7 gap-1 mb-2 text-center text-sm font-medium">
          {daysOfWeek.map((dayName) => (
            <div key={dayName} className="py-2">
              {dayName}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 h-[600px]">
          {days.map((day, i) => {
            const dayEvents = events.filter(event => {
              const eventStart = new Date(event.start_date);
              const eventEnd = new Date(event.end_date);
              return isSameDay(day, eventStart) || (day >= eventStart && day <= eventEnd);
            });
            
            return (
              <div
                key={i}
                className={`min-h-[100px] border rounded-md p-1 relative ${
                  isSameMonth(day, currentDate) 
                    ? 'bg-card'
                    : 'bg-muted/50 text-muted-foreground'
                } ${
                  isSameDay(day, new Date()) 
                    ? 'border-primary'
                    : 'border-border'
                }`}
              >
                <div className="text-right text-sm p-1 sticky top-0 bg-card z-10">
                  {format(day, 'd')}
                </div>
                <div className="flex flex-col gap-1 overflow-y-auto max-h-[80px]">
                  {dayEvents.map(event => (
                    <div
                      key={event.id}
                      onClick={() => handleViewEvent(event)}
                      className={`text-xs text-white px-1 py-0.5 rounded truncate cursor-pointer ${
                        EVENT_COLORS[event.related_type]
                      }`}
                    >
                      {event.title}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCalendarWeek = () => {
    const startDay = startOfWeek(currentDate, { weekStartsOn: 0 });
    const days = [];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    for (let i = 0; i < 7; i++) {
      days.push(addDays(startDay, i));
    }
    
    return (
      <div className="w-full overflow-x-auto">
        <div className="grid grid-cols-8 gap-1 mb-2">
          <div className="w-20 text-right pr-2 font-medium">Hora</div>
          {days.map((day, i) => (
            <div key={i} className="text-center">
              <div className="font-medium">{format(day, 'EEE', { locale: ptBR })}</div>
              <div className={`text-sm ${
                isSameDay(day, new Date()) ? 'text-primary font-bold' : ''
              }`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-8 gap-1 h-[600px] overflow-y-auto">
          {hours.map(hour => (
            <React.Fragment key={hour}>
              <div className="w-20 text-right pr-2 py-2 text-sm border-t">
                {hour}:00
              </div>
              {days.map((day, dayIndex) => {
                const hourEvents = events.filter(event => {
                  const eventStart = new Date(event.start_date);
                  return isSameDay(day, eventStart) && eventStart.getHours() === hour;
                });
                
                return (
                  <div 
                    key={dayIndex} 
                    className={`border-t relative min-h-[50px] ${
                      isSameDay(day, new Date()) ? 'bg-primary/5' : ''
                    }`}
                  >
                    {hourEvents.map(event => (
                      <div
                        key={event.id}
                        onClick={() => handleViewEvent(event)}
                        className={`absolute w-full text-xs text-white px-1 py-0.5 rounded cursor-pointer ${
                          EVENT_COLORS[event.related_type]
                        }`}
                      >
                        {event.title}
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderCalendarDay = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    return (
      <div className="w-full">
        <div className="text-center font-bold mb-4">
          {format(currentDate, 'PPPP', { locale: ptBR })}
        </div>
        
        <div className="space-y-1 h-[600px] overflow-y-auto">
          {hours.map(hour => {
            const hourEvents = events.filter(event => {
              const eventStart = new Date(event.start_date);
              return isSameDay(currentDate, eventStart) && eventStart.getHours() === hour;
            });
            
            return (
              <div key={hour} className="grid grid-cols-6 border-t">
                <div className="col-span-1 py-3 text-right pr-4">
                  {hour}:00
                </div>
                <div className="col-span-5 py-2 relative min-h-[60px]">
                  {hourEvents.map(event => (
                    <div
                      key={event.id}
                      onClick={() => handleViewEvent(event)}
                      className={`text-white px-2 py-1 my-1 rounded cursor-pointer ${
                        EVENT_COLORS[event.related_type]
                      }`}
                    >
                      <div className="font-medium">{event.title}</div>
                      <div className="text-xs opacity-90">
                        {format(new Date(event.start_date), 'HH:mm')} - 
                        {format(new Date(event.end_date), 'HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Calendário</h1>
          <p className="text-muted-foreground">Gerencie seus eventos e compromissos</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentDate(addMonths(currentDate, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => setCurrentDate(new Date())}
          >
            Hoje
          </Button>
          
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          <div className="text-xl font-medium ml-2">
            {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
          </div>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="month">Mês</TabsTrigger>
            <TabsTrigger value="week">Semana</TabsTrigger>
            <TabsTrigger value="day">Dia</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <Dialog open={showNewEventModal} onOpenChange={setShowNewEventModal}>
          <DialogTrigger asChild>
            <Button className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Novo Evento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Criar Novo Evento</DialogTitle>
              <DialogDescription>
                Preencha os detalhes para adicionar um novo evento ao calendário.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="title">Título</Label>
                <Input 
                  id="title" 
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  placeholder="Título do evento" 
                />
              </div>
              
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  placeholder="Descrição do evento"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Data de Início</Label>
                  <Input
                    id="start_date"
                    type="datetime-local"
                    value={format(new Date(newEvent.start_date), "yyyy-MM-dd'T'HH:mm")}
                    onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="end_date">Data de Término</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    value={format(new Date(newEvent.end_date), "yyyy-MM-dd'T'HH:mm")}
                    onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="all_day"
                  checked={newEvent.all_day}
                  onCheckedChange={(checked) => setNewEvent({...newEvent, all_day: checked})}
                />
                <Label htmlFor="all_day">Evento de dia inteiro</Label>
              </div>
              
              <div>
                <Label htmlFor="related_type">Tipo de Evento</Label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="meeting">Reunião</option>
                  <option value="task">Tarefa</option>
                  <option value="reminder">Lembrete</option>
                </select>
              </div>
              
              {(newEvent.related_type === 'ticket' || newEvent.related_type === 'project') && (
                <div>
                  <Label htmlFor="related_id">Item Relacionado</Label>
                </div>
              )}
              
              <div>
                <Label htmlFor="location">Local</Label>
                <Input
                  id="location"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                  placeholder="Local do evento (presencial ou virtual)"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="notification"
                  checked={newEvent.notification}
                  onCheckedChange={(checked) => setNewEvent({...newEvent, notification: checked})}
                />
                <Label htmlFor="notification">Enviar notificação</Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewEventModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateEvent}>
                Salvar Evento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <Card>
        <CardContent className="pt-6">
          <div className="h-[calc(100vh-300px)] overflow-auto">
            {view === 'month' && renderCalendarMonth()}
            {view === 'week' && renderCalendarWeek()}
            {view === 'day' && renderCalendarDay()}
          </div>
        </CardContent>
      </Card>

      {/* Dialog para exibir detalhes do evento */}
      <Dialog open={showEventDetailModal} onOpenChange={setShowEventDetailModal}>
        {selectedEvent && (
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${EVENT_COLORS[selectedEvent.related_type].split(' ')[0]}`} />
                {selectedEvent.title}
              </DialogTitle>
              <DialogDescription>
                Visualize os detalhes completos deste evento e suas informações relacionadas.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              {selectedEvent.description && (
                <div>
                  <Label className="text-muted-foreground">Descrição</Label>
                  <p>{selectedEvent.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Início</Label>
                  <p className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {format(new Date(selectedEvent.start_date), 'PPp', { locale: ptBR })}
                  </p>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Término</Label>
                  <p className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {format(new Date(selectedEvent.end_date), 'PPp', { locale: ptBR })}
                  </p>
                </div>
              </div>
              
              {selectedEvent.location && (
                <div>
                  <Label className="text-muted-foreground">Local</Label>
                  <p>{selectedEvent.location}</p>
                </div>
              )}
              
              <div>
                <Label className="text-muted-foreground">Tipo</Label>
                <Badge className={`${EVENT_COLORS[selectedEvent.related_type].split(' ')[0]} text-white mt-1`}>
                  {selectedEvent.related_type === 'ticket' ? 'Chamado' :
                    selectedEvent.related_type === 'project' ? 'Projeto' :
                    selectedEvent.related_type === 'meeting' ? 'Reunião' :
                    selectedEvent.related_type === 'internal' ? 'Interno' :
                    selectedEvent.related_type === 'external' ? 'Externo' : 'Outro'
                  }
                </Badge>
              </div>
              
              {selectedEvent.related_id && (selectedEvent.related_type === 'ticket' || selectedEvent.related_type === 'project') && (
                <div>
                  <Label className="text-muted-foreground">
                    {selectedEvent.related_type === 'ticket' ? 'Chamado Relacionado' : 'Projeto Relacionado'}
                  </Label>
                  <Button 
                    variant="link" 
                    className="p-0 h-auto font-normal"
                    onClick={() => {
                      setShowEventDetailModal(false);
                      if (selectedEvent.related_type === 'ticket') {
                        navigate(createPageUrl(`ViewTicket?id=${selectedEvent.related_id}`));
                      } else {
                        navigate(createPageUrl(`ViewProject?id=${selectedEvent.related_id}`));
                      }
                    }}
                  >
                    {selectedEvent.related_type === 'ticket' ? 
                      `#${selectedEvent.related_id.substring(0, 8)}` : 
                      `${relatedItems.projects.find(p => p.id === selectedEvent.related_id)?.name || 'Ver projeto'}`
                    }
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
            </div>
            
            <DialogFooter className="gap-2">
              <Button 
                variant="destructive"
                onClick={() => handleDeleteEvent(selectedEvent.id)}
              >
                Excluir
              </Button>
              <Button onClick={() => setShowEventDetailModal(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
